import { useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Plus, Trash2, MapPin, Pencil, Search } from "lucide-react";
import { toast } from "sonner";

/** Locations (Hospitals) master with add + edit + delete + geo-capture. */
export default function LocationsTab({ locs, onChange }) {
  const [newLoc, setNewLoc] = useState({ name: "", code: "", lat: "", lng: "", radius_m: 500, address: "" });
  const [editing, setEditing] = useState(null); // location object being edited
  const [q, setQ] = useState("");

  const filtered = useMemo(() => {
    if (!q) return locs;
    const s = q.toLowerCase();
    return locs.filter(l => (l.name || "").toLowerCase().includes(s) || (l.code || "").toLowerCase().includes(s));
  }, [locs, q]);

  async function add() {
    if (!newLoc.name.trim()) { toast.error("Name required"); return; }
    await api.post("/masters/locations", {
      name: newLoc.name.trim(),
      code: newLoc.code.trim(),
      lat: parseFloat(newLoc.lat || 0),
      lng: parseFloat(newLoc.lng || 0),
      radius_m: parseInt(newLoc.radius_m || 500),
      address: newLoc.address || "",
    });
    setNewLoc({ name: "", code: "", lat: "", lng: "", radius_m: 500, address: "" });
    onChange();
    toast.success("Location added");
  }

  async function save() {
    if (!editing) return;
    await api.put(`/masters/locations/${editing.id}`, {
      name: editing.name,
      code: editing.code || "",
      lat: parseFloat(editing.lat || 0),
      lng: parseFloat(editing.lng || 0),
      radius_m: parseInt(editing.radius_m || 500),
      address: editing.address || "",
    });
    setEditing(null);
    onChange();
    toast.success("Location updated");
  }

  async function del(id) {
    if (!window.confirm("Delete this location? Employees & departments mapped to it will keep the reference until unset.")) return;
    await api.delete(`/masters/locations/${id}`);
    onChange();
  }

  function captureCoords(setter, obj) {
    if (!navigator.geolocation) { toast.error("Geolocation not supported"); return; }
    navigator.geolocation.getCurrentPosition((p) => {
      setter({ ...obj, lat: p.coords.latitude.toFixed(6), lng: p.coords.longitude.toFixed(6) });
      toast.success("Captured current coordinates");
    }, () => toast.error("Location denied"));
  }

  return (
    <Card className="border-slate-200 rounded-2xl">
      <CardContent className="p-6 space-y-4">
        {/* Add new */}
        <div className="p-4 rounded-2xl bg-slate-50 border border-slate-200 space-y-3" data-testid="loc-new-card">
          <div className="text-xs uppercase tracking-wider text-slate-500 font-bold">Add Hospital / Location</div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-6 gap-2">
            <Input placeholder="Site name*" value={newLoc.name} onChange={e => setNewLoc({ ...newLoc, name: e.target.value })} data-testid="loc-new-name"/>
            <Input placeholder="Code (e.g. HOSP-01)" value={newLoc.code} onChange={e => setNewLoc({ ...newLoc, code: e.target.value })} data-testid="loc-new-code"/>
            <Input placeholder="Latitude" value={newLoc.lat} onChange={e => setNewLoc({ ...newLoc, lat: e.target.value })}/>
            <Input placeholder="Longitude" value={newLoc.lng} onChange={e => setNewLoc({ ...newLoc, lng: e.target.value })}/>
            <Input placeholder="Radius (m)" value={newLoc.radius_m} onChange={e => setNewLoc({ ...newLoc, radius_m: e.target.value })}/>
            <Button onClick={add} className="bg-sky-500 hover:bg-sky-600" data-testid="loc-new-add"><Plus className="h-4 w-4 mr-1"/>Add</Button>
          </div>
          <Input placeholder="Address (optional)" value={newLoc.address} onChange={e => setNewLoc({ ...newLoc, address: e.target.value })}/>
          <Button variant="outline" size="sm" onClick={() => captureCoords(setNewLoc, newLoc)}>
            <MapPin className="h-4 w-4 mr-1.5"/>Use my current coordinates
          </Button>
        </div>

        {/* Search */}
        <div className="relative">
          <Search className="h-4 w-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2"/>
          <Input value={q} onChange={e => setQ(e.target.value)} placeholder="Search locations…" className="pl-9" data-testid="loc-search"/>
        </div>

        {/* List */}
        <div className="grid sm:grid-cols-2 gap-3">
          {filtered.length === 0 && <div className="text-center text-slate-400 py-8 col-span-full">No locations.</div>}
          {filtered.map(l => {
            const isEditing = editing?.id === l.id;
            if (isEditing) {
              return (
                <div key={l.id} className="p-4 rounded-2xl border border-sky-300 bg-sky-50 space-y-2">
                  <div className="grid grid-cols-2 gap-2">
                    <div><Label className="text-xs">Name</Label><Input value={editing.name} onChange={e => setEditing({ ...editing, name: e.target.value })}/></div>
                    <div><Label className="text-xs">Code</Label><Input value={editing.code || ""} onChange={e => setEditing({ ...editing, code: e.target.value })}/></div>
                    <div><Label className="text-xs">Latitude</Label><Input value={editing.lat} onChange={e => setEditing({ ...editing, lat: e.target.value })}/></div>
                    <div><Label className="text-xs">Longitude</Label><Input value={editing.lng} onChange={e => setEditing({ ...editing, lng: e.target.value })}/></div>
                    <div><Label className="text-xs">Radius (m)</Label><Input value={editing.radius_m} onChange={e => setEditing({ ...editing, radius_m: e.target.value })}/></div>
                  </div>
                  <Label className="text-xs">Address</Label>
                  <Input value={editing.address || ""} onChange={e => setEditing({ ...editing, address: e.target.value })}/>
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline" onClick={() => captureCoords(setEditing, editing)}><MapPin className="h-3.5 w-3.5 mr-1"/>Use my coords</Button>
                    <div className="ml-auto flex gap-1.5">
                      <Button size="sm" variant="outline" onClick={() => setEditing(null)}>Cancel</Button>
                      <Button size="sm" onClick={save} className="bg-emerald-600 hover:bg-emerald-700" data-testid={`loc-save-${l.id}`}>Save</Button>
                    </div>
                  </div>
                </div>
              );
            }
            return (
              <div key={l.id} className="p-4 rounded-2xl border border-slate-200 bg-white flex items-start justify-between" data-testid={`loc-row-${l.id}`}>
                <div>
                  <div className="font-bold text-slate-900">
                    {l.name}
                    {l.code && <span className="ml-2 text-xs font-mono text-slate-400">{l.code}</span>}
                  </div>
                  <div className="text-xs text-slate-500 mt-1">
                    {(l.lat || l.lng) ? `${(l.lat || 0).toFixed(4)}, ${(l.lng || 0).toFixed(4)}` : "no coords"} · {l.radius_m || 0}m radius
                  </div>
                  {l.address && <div className="text-xs text-slate-400 mt-1">{l.address}</div>}
                </div>
                <div className="flex items-center gap-1">
                  <Button variant="ghost" size="sm" onClick={() => setEditing(l)} data-testid={`loc-edit-${l.id}`}><Pencil className="h-4 w-4"/></Button>
                  <Button variant="ghost" size="sm" onClick={() => del(l.id)} data-testid={`loc-delete-${l.id}`}><Trash2 className="h-4 w-4 text-red-500"/></Button>
                </div>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}
